<script lang="ts" setup>
import { ref, Ref, computed, watch, onMounted } from 'vue';
import store from '@/store';
import { useI18n } from 'vue-i18n';

import {
    MultiSelectOption,
    UserGetters,
    UserActions,
    UserFilter,
    SelectOptionsServiceGetRolesResult,
} from '@/types';
import { FilterIcon } from '@heroicons/vue/solid';
import { SelectOptionsService } from '@/services';
import { toastServiceError } from '@/services/core/notification';

const { t } = useI18n();

const filtersOpened = ref(false);

const defaultFilter = computed(() => store.getters[UserGetters.DEFAULT_FILTER]);

const filter: Ref<UserFilter> = ref({
    roles: defaultFilter.value.roles,
    isActive: defaultFilter.value.isActive,
    useAutoAttendant: defaultFilter.value.useAutoAttendant,
});

const storeFilters = computed<UserFilter>(() => store.getters[UserGetters.FILTER]);

const isActiveFilterOptions = [
    {
        label: t('user.filters.isActive.options.active'),
        value: true,
    },
    {
        label: t('user.filters.isActive.options.inactive'),
        value: false,
    },
] as MultiSelectOption[];

// Reuse same options for useAutoAttendant (active/inactive)
const useAutoAttendantOptions = isActiveFilterOptions;

const rolesOptions: Ref<SelectOptionsServiceGetRolesResult[]> = ref([]);
const getRoles = async () => {
    try {
        const response = await SelectOptionsService.getRoles();
        rolesOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

onMounted(() => {
    getRoles();
});

function applyFilters() {
    store.dispatch(UserActions.SET_FILTER, filter.value);
}

function clearFilters() {
    store.dispatch(UserActions.CLEAR_FILTER);
    refreshLocalFilter();
}

function refreshLocalFilter() {
    filter.value.roles = storeFilters.value.roles;
    filter.value.isActive = storeFilters.value.isActive;
}

watch(filtersOpened, () => {
    refreshLocalFilter();
});
</script>

<template>
    <UiRightPanel v-model="filtersOpened" :title="$t('filter.sidebar.title')">
        <template #button>
            <UiButton
                size="md"
                variant="white"
                text-variant="gray-700"
                class="hover:bg-gray-50"
                @click="filtersOpened = true"
            >
                <FilterIcon class="mr-0 h-5 w-5 text-gray-400 sm:mr-3" aria-hidden="true" />
                <span class="hidden sm:block">{{ $t('core.actions.Filter') }}</span>
            </UiButton>
        </template>
        <template #default>
            <form autocomplete="off" class="flex flex-col gap-3">
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-6">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('user.filters.roles') }}
                        </label>
                        <UiMultiselect
                            name="filter_role"
                            label="name"
                            value-prop="id"
                            multiple
                            v-model="filter.roles"
                            :options="rolesOptions"
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                    <div class="sm:col-span-4">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('user.filters.isActive.label') }}
                        </label>
                        <UiMultiselect
                            name="filters_isActive"
                            v-model="filter.isActive"
                            :options="isActiveFilterOptions"
                            can-clear
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                    <div class="sm:col-span-4">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('user.labels.useAutoAttendant') }}
                        </label>
                        <UiMultiselect
                            name="filters_useAutoAttendant"
                            v-model="filter.useAutoAttendant"
                            :options="useAutoAttendantOptions"
                            can-clear
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                </div>
                <div class="mt-4 grid grid-cols-2 place-content-around gap-4 text-center">
                    <UiButton variant="white" text-variant="gray-700" @click="clearFilters()">
                        {{ $t('core.actions.Clear') }}
                    </UiButton>
                    <UiButton variant="primary" text-variant="white" @click="applyFilters()">
                        {{ $t('core.actions.Apply') }}
                    </UiButton>
                </div>
            </form>
        </template>
    </UiRightPanel>
</template>
